import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import Lightbox from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import Fullscreen from 'yet-another-react-lightbox/plugins/fullscreen';
import Counter from 'yet-another-react-lightbox/plugins/counter';
import Captions from 'yet-another-react-lightbox/plugins/captions';
import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/counter.css';
import 'yet-another-react-lightbox/plugins/captions.css';
import { flattenToAppURL, isInternalURL } from '@plone/volto/helpers/Url/Url';

const messages = defineMessages({
  close: {
    id: 'imageGalleryLightboxClose',
    defaultMessage: 'Close',
  },
  previous: {
    id: 'imageGalleryLightboxPrevious',
    defaultMessage: 'Previous',
  },
  next: {
    id: 'imageGalleryLightboxNext',
    defaultMessage: 'Next',
  },
  zoomIn: {
    id: 'imageGalleryLightboxZoomIn',
    defaultMessage: 'Zoom in',
  },
  zoomOut: {
    id: 'imageGalleryLightboxZoomOut',
    defaultMessage: 'Zoom out',
  },
  fullscreenEnter: {
    id: 'imageGalleryLightboxFullscreenEnter',
    defaultMessage: 'Fullscreen',
  },
  fullscreenExit: {
    id: 'imageGalleryLightboxFullscreenExit',
    defaultMessage: 'Exit fullscreen',
  },
});

// Pick the largest available scale from image_scales as the lightbox source.
// image_scales shape: { <field>: [ { scale: 'large', href, width, height }, ... ] }
// Falls back to `${appUrl}/@@images/<field>/huge` then the raw url.
const resolveFullsizeSrc = (img) => {
  if (!img) return null;
  if (img.image_scales && img.image_field) {
    const scales = img.image_scales[img.image_field];
    if (Array.isArray(scales) && scales.length > 0) {
      // Prefer the largest scale by width; YARL will size down responsively.
      const largest = [...scales].sort(
        (a, b) => (b.width || 0) - (a.width || 0),
      )[0];
      if (largest?.download) {
        return isInternalURL(largest.download)
          ? flattenToAppURL(largest.download)
          : largest.download;
      }
    }
    // Fallback to the @@images huge scale on the content item.
    return `${flattenToAppURL(img.url)}/@@images/${img.image_field}/huge`;
  }
  if (img.url) {
    return isInternalURL(img.url)
      ? `${flattenToAppURL(img.url)}/@@images/image/huge`
      : img.url;
  }
  return null;
};

const ImageGalleryLightbox = ({
  images,
  index,
  onClose,
  zoom = true,
  fullscreen = true,
  counter = true,
}) => {
  const intl = useIntl();

  const slides = (images || []).map((img) => ({
    src: resolveFullsizeSrc(img),
    alt: img.alt || '',
    title: img.caption || undefined,
    description: img.caption || undefined,
  }));

  const plugins = [Captions]; // Captions always on
  if (zoom) plugins.push(Zoom);
  if (fullscreen) plugins.push(Fullscreen);
  if (counter) plugins.push(Counter);

  const labels = {
    Close: intl.formatMessage(messages.close),
    Previous: intl.formatMessage(messages.previous),
    Next: intl.formatMessage(messages.next),
    'Zoom in': intl.formatMessage(messages.zoomIn),
    'Zoom out': intl.formatMessage(messages.zoomOut),
    'Enter fullscreen': intl.formatMessage(messages.fullscreenEnter),
    'Exit fullscreen': intl.formatMessage(messages.fullscreenExit),
  };

  return (
    <Lightbox
      open={index !== null && index !== undefined}
      close={onClose}
      slides={slides}
      index={index || 0}
      plugins={plugins}
      labels={labels}
      carousel={{ finite: false }}
      controller={{ closeOnBackdropClick: true }}
      animation={{ fade: 250, swipe: 500 }}
    />
  );
};

export default ImageGalleryLightbox;
